import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { validateInviteCode } from '../../utils/inviteService';
import { Business } from '../../types/business';
import { 
  Loader2, 
  Building2, 
  LogIn, 
  UserPlus, 
  AlertCircle,
  ArrowLeft,
  MapPin,
  Phone
} from 'lucide-react';
import LoginForm from './LoginForm';
import CreateUser from './CreateUser';

type ViewMode = 'landing' | 'login' | 'register';

const BusinessLandingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('landing');

  // Cargar datos del negocio
  useEffect(() => {
    const loadBusiness = async () => {
      if (!slug) {
        setError('Enlace inválido');
        setLoading(false);
        return;
      }

      try {
        const businessData = await validateInviteCode(slug);
        if (businessData) {
          setBusiness(businessData);
        } else {
          setError('No se encontró el negocio. Verifica que el enlace sea correcto.');
        }
      } catch (err: any) {
        console.error('Error loading business:', err);
        setError(err.message || 'Error al cargar el negocio');
      } finally {
        setLoading(false);
      }
    };

    loadBusiness();
  }, [slug]);

  // Verificar si el usuario ya está logueado
  useEffect(() => {
    if (authLoading || loading || !business) return;

    if (currentUser) {
      // Usuario ya logueado
      if (currentUser.businessId === business.id) {
        // Es del mismo negocio - redirigir al dashboard
        const redirectPath = currentUser.role === 'admin' 
          ? '/admin/dashboard' 
          : currentUser.role === 'instructor'
            ? '/instructor'
            : '/dashboard';
        navigate(redirectPath, { replace: true });
      }
      // Si es de otro negocio, se mostrará el mensaje en el render
    }
  }, [currentUser, authLoading, loading, business, navigate]);

  // Estado de carga
  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Cargando...</p>
        </div>
      </div>
    );
  }

  // Error - negocio no encontrado
  if (error || !business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Negocio no encontrado</h2>
            <p className="text-gray-400 mb-6">{error || 'El enlace no es válido o ha expirado.'}</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Ir a Inicio de Sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Usuario ya logueado en OTRO negocio
  if (currentUser && currentUser.businessId !== business.id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Ya tienes una cuenta</h2>
            <p className="text-gray-400 mb-4">
              Tu cuenta está asociada a otro negocio. Cada email solo puede estar registrado en un negocio.
            </p>
            <p className="text-amber-400 text-sm mb-6">
              Si quieres registrarte en <strong>{business.name}</strong>, debes usar un email diferente.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-colors"
              >
                Ir a mi cuenta
              </button>
              <button
                onClick={async () => {
                  await signOut(auth);
                  setViewMode('register');
                }}
                className="w-full px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors"
              >
                Cerrar sesión y registrarme aquí
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar vista de Login o Register
  if (viewMode === 'login' || viewMode === 'register') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 py-12 px-4">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-lg w-full">
          {/* Header con info del negocio */}
          <div className="text-center mb-6">
            <button
              onClick={() => setViewMode('landing')}
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
            {business.logo ? (
              <img 
                src={business.logo} 
                alt={business.name}
                className="w-20 h-20 object-contain mx-auto mb-3 rounded-xl"
              />
            ) : (
              <div className="w-20 h-20 bg-amber-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Building2 className="w-10 h-10 text-amber-400" />
              </div>
            )}
            <h1 className="text-xl font-bold text-white">{business.name}</h1>
            <p className="text-gray-400 text-sm mt-1">
              {viewMode === 'login' ? 'Inicia sesión en tu cuenta' : 'Crea tu cuenta'}
            </p>
          </div>

          {/* Formulario */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8">
            {viewMode === 'login' ? (
              <LoginForm />
            ) : (
              <CreateUser preloadedBusiness={business} />
            )}
          </div>

          {/* Toggle entre login y register */}
          <div className="text-center mt-6">
            {viewMode === 'login' ? (
              <p className="text-gray-400">
                ¿No tienes cuenta?{' '}
                <button 
                  onClick={() => setViewMode('register')}
                  className="text-amber-400 hover:text-amber-300 font-medium"
                >
                  Regístrate
                </button>
              </p>
            ) : (
              <p className="text-gray-400">
                ¿Ya tienes cuenta?{' '}
                <button 
                  onClick={() => setViewMode('login')}
                  className="text-amber-400 hover:text-amber-300 font-medium"
                >
                  Inicia sesión
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Vista Landing del negocio
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 py-12 px-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-md w-full">
        {/* Card del negocio */}
        <div className="bg-gray-900 rounded-3xl border border-gray-800 overflow-hidden shadow-2xl">
          {/* Header con logo/imagen */}
          <div className="bg-gradient-to-br from-amber-600 to-orange-600 p-8 text-center">
            {business.logo ? (
              <img 
                src={business.logo} 
                alt={business.name}
                className="w-24 h-24 object-contain mx-auto mb-4 rounded-2xl bg-white/10 p-2"
              />
            ) : (
              <div className="w-24 h-24 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-12 h-12 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-white">{business.name}</h1>
            {(business.address || business.phone) && (
              <div className="mt-3 space-y-1">
                {business.address && (
                  <p className="text-white/80 text-sm flex items-center justify-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {business.address}
                  </p>
                )}
                {business.phone && (
                  <p className="text-white/80 text-sm flex items-center justify-center gap-1">
                    <Phone className="w-3 h-3" />
                    {business.phone}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Botones de acción */}
          <div className="p-6 space-y-4">
            <p className="text-center text-gray-400 mb-6">
              Accede a tu cuenta o regístrate para reservar clases
            </p>

            <button
              onClick={() => setViewMode('login')}
              className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-amber-600/25"
            >
              <LogIn className="w-5 h-5" />
              Iniciar Sesión
            </button>

            <button
              onClick={() => setViewMode('register')}
              className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-xl transition-colors border border-gray-700"
            >
              <UserPlus className="w-5 h-5" />
              Crear Cuenta
            </button>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-800 px-6 py-4">
            <p className="text-center text-gray-500 text-xs">
              Powered by{' '}
              <span className="text-amber-400">Reserberry Sport</span>
            </p>
          </div>
        </div>

        {/* Link discreto a login general */}
        <div className="text-center mt-6">
          <Link to="/login" className="text-gray-500 hover:text-gray-400 text-sm">
            ¿Buscas otro negocio? Ir al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BusinessLandingPage;

